import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Users,
  Target,
  Trash2
} from 'lucide-react';
import { fetchSessions, fetchAnalyticsSessions, deleteLeadSourceSessions } from '../services/adminService';
import { AdminSession } from '../types/admin.types';

interface LeadSourceStats {
  source: string;
  totalVisits: number;
  completedSessions: number;
  conversionRate: number;
  totalSavings: number;
}

const LeadSourceAnalytics: React.FC = () => {
  const [leadSourceStats, setLeadSourceStats] = useState<LeadSourceStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [sourceSessions, setSourceSessions] = useState<AdminSession[]>([]);
  const [deleteConfirmSource, setDeleteConfirmSource] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadLeadSourceData();
  }, []);

  const loadLeadSourceData = async () => {
    setLoading(true);
    try {
      // Get all analytics sessions (includes anonymous visitors)
      const analyticsSessions = await fetchAnalyticsSessions();
      
      // Get all completed sessions
      const completedSessions = await fetchSessions(undefined, true);
      
      // Group by lead source
      const sourceMap = new Map<string, LeadSourceStats>();
      
      // Count all visits per source
      analyticsSessions.forEach(session => {
        if (session.leadSource) {
          if (!sourceMap.has(session.leadSource)) {
            sourceMap.set(session.leadSource, {
              source: session.leadSource,
              totalVisits: 0,
              completedSessions: 0,
              conversionRate: 0,
              totalSavings: 0
            });
          }
          const stats = sourceMap.get(session.leadSource)!;
          stats.totalVisits++;
        }
      });
      
      // Count completed sessions and savings per source
      completedSessions.forEach(session => {
        if (session.leadSource && sourceMap.has(session.leadSource)) {
          const stats = sourceMap.get(session.leadSource)!;
          if (session.status === 'finalized' || session.status === 'complete') {
            stats.completedSessions++;
            stats.totalSavings += session.totalAnnualSavings || 0;
          }
        }
      });
      
      // Calculate conversion rates
      sourceMap.forEach(stats => {
        stats.conversionRate = stats.totalVisits > 0 
          ? (stats.completedSessions / stats.totalVisits) * 100 
          : 0;
      });
      
      // Convert to array and sort by total visits
      const statsArray = Array.from(sourceMap.values());
      statsArray.sort((a, b) => b.totalVisits - a.totalVisits);
      
      setLeadSourceStats(statsArray);
    } catch (error) {
      console.error('Error loading lead source data:', error);
    }
    setLoading(false);
  };

  const loadSourceSessions = async (source: string) => {
    try {
      const sessions = await fetchSessions({ leadSource: source }, true);
      setSourceSessions(sessions);
      setSelectedSource(source);
    } catch (error) {
      console.error('Error loading source sessions:', error);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteLeadSource = async (source: string) => {
    setIsDeleting(true);
    try {
      const result = await deleteLeadSourceSessions(source);

      if (result.success) {
        // Clear the selected source if it's the one being deleted
        if (selectedSource === source) {
          setSelectedSource(null);
          setSourceSessions([]);
        }

        // Reload the data after successful deletion
        await loadLeadSourceData();
        setDeleteConfirmSource(null);
      } else {
        // Show error message
        console.error('Failed to delete lead source:', result.errors);
        alert('Failed to delete lead source. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting lead source:', error);
      alert('An error occurred while deleting the lead source.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Lead Source Analytics</h2>
        <p className="text-gray-600">Track visitor sources and conversion rates</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="h-5 w-5 text-blue-500" />
            <span className="text-sm text-gray-500">Total Sources</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{leadSourceStats.length}</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <Target className="h-5 w-5 text-green-500" />
            <span className="text-sm text-gray-500">Total Visits</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {leadSourceStats.reduce((sum, s) => sum + s.totalVisits, 0)}
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="h-5 w-5 text-purple-500" />
            <span className="text-sm text-gray-500">Avg Conversion</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {leadSourceStats.length > 0 
              ? (leadSourceStats.reduce((sum, s) => sum + s.conversionRate, 0) / leadSourceStats.length).toFixed(1)
              : 0}%
          </p>
        </div>
      </div>

      {/* Lead Source Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Lead Sources</h3>
        </div>
        
        {leadSourceStats.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">No lead sources tracked yet</p>
            <p className="text-sm text-gray-400 mt-2">
              Use ?source=yourSource in URLs to track lead sources
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Visits
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Savings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leadSourceStats.map((stats) => (
                  <tr key={stats.source} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-700">
                        {stats.source}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stats.totalVisits}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stats.completedSessions}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {stats.conversionRate.toFixed(1)}%
                        </span>
                        <div className="ml-2 w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${Math.min(stats.conversionRate, 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${Math.round(stats.totalSavings).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => loadSourceSessions(stats.source)}
                          className="text-sky-600 hover:text-sky-700 font-medium"
                        >
                          View Sessions
                        </button>
                        <button
                          onClick={() => setDeleteConfirmSource(stats.source)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete Lead Source"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Selected Source Sessions */}
      {selectedSource && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Sessions from "{selectedSource}"
            </h3>
            <button
              onClick={() => {
                setSelectedSource(null);
                setSourceSessions([]);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
          
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {sourceSessions.map((session) => (
              <div key={session.sessionId} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {session.userName || 'Anonymous Visitor'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {session.userEmail !== 'No email provided' ? session.userEmail : 'No email'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(session.startedAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${session.status === 'finalized' ? 'bg-green-100 text-green-800' :
                        session.status === 'complete' ? 'bg-blue-100 text-blue-800' :
                        session.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'}`}>
                      {session.status}
                    </span>
                    {session.currentTAS && (
                      <p className="text-sm text-gray-600 mt-1">
                        TAS: {Math.round(session.currentTAS)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmSource && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

            {/* Center the modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Trash2 className="h-6 w-6 text-red-600" aria-hidden="true" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Delete Lead Source
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete the lead source <strong>"{deleteConfirmSource}"</strong>?
                    </p>
                    {(() => {
                      const stats = leadSourceStats.find(s => s.source === deleteConfirmSource);
                      return stats ? (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
                          <p className="text-sm text-red-800 font-medium">This action will affect:</p>
                          <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                            <li>{stats.totalVisits} visitor session{stats.totalVisits !== 1 ? 's' : ''}</li>
                            <li>{stats.completedSessions} completed assessment{stats.completedSessions !== 1 ? 's' : ''}</li>
                          </ul>
                          <p className="mt-2 text-xs text-red-600">
                            This action cannot be undone.
                          </p>
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => handleDeleteLeadSource(deleteConfirmSource)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  type="button"
                  disabled={isDeleting}
                  onClick={() => setDeleteConfirmSource(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadSourceAnalytics;